import {
    canApplyToSubvencio,
    getSubvencioApplyDisabledReason,
} from './subvencionsGrantAvailability';
import {
    getSubvencioInfoHref,
    getSubvencioInfoUrl,
} from './subvencionsGrantLinks';

const ACTION_VARIANTS = {
    card: {
        minWidth: 140,
        gap: 6,
        primaryPadding: '10px 18px',
        secondaryPadding: '9px 18px',
        primaryFontSize: 14,
        secondaryFontSize: 13,
        primaryWeight: 600,
        secondaryWeight: 600,
    },
    detail: {
        minWidth: 180,
        gap: 8,
        primaryPadding: '14px 18px',
        secondaryPadding: '12px 18px',
        primaryFontSize: 14,
        secondaryFontSize: 14,
        primaryWeight: 700,
        secondaryWeight: 700,
    },
};

function buildPrimaryStyle({ canApply, color, variantConfig }) {
    return {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: variantConfig.primaryPadding,
        backgroundColor: canApply ? color : '#e0e0e0',
        color: canApply ? '#fff' : '#666',
        border: 'none',
        borderRadius: 8,
        textDecoration: 'none',
        fontSize: variantConfig.primaryFontSize,
        fontWeight: variantConfig.primaryWeight,
        whiteSpace: 'nowrap',
        cursor: canApply ? 'pointer' : 'not-allowed',
        fontFamily: 'inherit',
        opacity: canApply ? 1 : 0.75,
    };
}

function buildSecondaryStyle({ color, variantConfig }) {
    return {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: variantConfig.secondaryPadding,
        backgroundColor: 'transparent',
        color,
        border: `1px solid ${color}`,
        borderRadius: 8,
        textDecoration: 'none',
        fontSize: variantConfig.secondaryFontSize,
        fontWeight: variantConfig.secondaryWeight,
        whiteSpace: 'nowrap',
    };
}

function SubvencionsGrantActions({
    grant,
    color = '#0f766e',
    guideHref,
    variant = 'card',
    applyLabel,
    showApply = true,
    showInfo = true,
    onActionClick,
}) {
    const variantConfig = ACTION_VARIANTS[variant] || ACTION_VARIANTS.card;
    const directApplyHref = showApply ? (grant?.applyUrl || grant?.sourceUrl) : null;
    const applyHref = directApplyHref
        ? (getSubvencioInfoUrl(grant?.sourceUrl) || directApplyHref)
        : null;
    const canApply = canApplyToSubvencio(grant);
    const disabledReason = applyHref && !canApply ? getSubvencioApplyDisabledReason(grant) : null;
    const infoHref = showInfo ? getSubvencioInfoHref(grant, directApplyHref) : null;
    const resolvedApplyLabel = applyLabel || (variant === 'detail'
        ? 'Sol·licitar'
        : (grant?.status === 'waiting' ? 'Veure detalls' : 'Sol·licitar →'));
    const primaryStyle = buildPrimaryStyle({ canApply, color, variantConfig });
    const secondaryStyle = buildSecondaryStyle({ color, variantConfig });

    if (!applyHref && !infoHref && !guideHref) {
        return null;
    }

    return (
        <div
            className={`subvencions-grant-actions subvencions-grant-actions-${variant}`}
            onClick={onActionClick}
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: variantConfig.gap,
                flexShrink: 0,
                minWidth: variantConfig.minWidth,
            }}
        >
            {applyHref && canApply ? (
                <a
                    href={applyHref}
                    target="_blank"
                    rel="noreferrer"
                    style={primaryStyle}
                >
                    {resolvedApplyLabel}
                </a>
            ) : applyHref ? (
                <button
                    type="button"
                    disabled
                    title={disabledReason}
                    aria-label={`${resolvedApplyLabel}. ${disabledReason}`}
                    style={primaryStyle}
                >
                    {resolvedApplyLabel}
                </button>
            ) : null}
            {infoHref ? (
                <a
                    href={infoHref}
                    target="_blank"
                    rel="noreferrer"
                    style={secondaryStyle}
                >
                    Més informació
                </a>
            ) : null}
            {guideHref ? (
                <a
                    href={guideHref}
                    style={secondaryStyle}
                >
                    Veure guia
                </a>
            ) : null}
        </div>
    );
}

export default SubvencionsGrantActions;
